import { humaniseKey } from './qualityDimensions';

export const DEFAULT_STAR_FORMULA = {
  weights: { quality: 0.4, volume: 0.3, issue_free: 0.3, punctuality: 0 },
  quality_dimensions: {
    clarity: 0.2,
    engagement: 0.2,
    adaptability: 0.2,
    encouragement: 0.2,
    structure: 0.2,
  },
};

export const DEFAULT_NEEDS_ATTENTION = {
  late_minutes: 5,
  on_time_rate_below_pct: 60,
  quality_below_pct: 40,
  min_open_issues: 1,
  window_days: 30,
};

const clampWeights = (w, defaults) => {
  const out = { ...defaults, ...(w || {}) };
  Object.keys(out).forEach((k) => {
    const v = Number(out[k]);
    out[k] = Number.isFinite(v) && v >= 0 ? v : 0;
  });
  return out;
};

export const normalizeFormula = (raw) => {
  if (!raw || typeof raw !== 'object') return DEFAULT_STAR_FORMULA;
  return {
    weights: clampWeights(raw.weights, DEFAULT_STAR_FORMULA.weights),
    quality_dimensions: clampWeights(raw.quality_dimensions, DEFAULT_STAR_FORMULA.quality_dimensions),
  };
};

export const normalizeThresholds = (raw) => {
  if (!raw || typeof raw !== 'object') return DEFAULT_NEEDS_ATTENTION;
  const out = { ...DEFAULT_NEEDS_ATTENTION };
  Object.keys(out).forEach((k) => {
    const v = Number(raw[k]);
    if (Number.isFinite(v) && v >= 0) out[k] = v;
  });
  return out;
};

const sumValues = (obj) => Object.values(obj).reduce((a, b) => a + (Number(b) || 0), 0);

// A dimension counts towards the quality average only when the caller actually
// supplied a number for it. null / undefined / '' mean "this org's rubric has no
// such dimension, or nobody rated it" — those are excluded from the average
// instead of being folded in as a zero, which would drag the score down.
const isRatedValue = (v) =>
  v !== null && v !== undefined && v !== '' && typeof v !== 'boolean' && Number.isFinite(Number(v));

/**
 * Normalised per-dimension sub-weights for exactly the dimension keys given.
 *
 * - Keys the formula weights explicitly keep their weight.
 * - Keys the formula says nothing about (a custom rubric dimension such as
 *   `subject_mastery`) get the average of the explicit weights, so they are
 *   neither ignored nor over-weighted. With no explicit weights at all,
 *   everything shares equally.
 * - The result is renormalised so the returned weights sum to 1 across the keys
 *   passed in. If every weight is zero, all keys share equally.
 *
 * Returns { orderedKeys, weights } where orderedKeys follows the formula's own
 * key order first (so the default rubric sums in exactly the historical order).
 */
export const resolveDimensionWeights = (quality_dimensions, keys) => {
  const present = (Array.isArray(keys) ? keys : []).filter((k) => typeof k === 'string' && k);
  const q = quality_dimensions || {};
  const presentSet = new Set(present);
  const orderedKeys = [
    ...Object.keys(q).filter((k) => presentSet.has(k)),
    ...present.filter((k) => !Object.prototype.hasOwnProperty.call(q, k)),
  ];

  if (orderedKeys.length === 0) return { orderedKeys, weights: {} };

  const equalShare = 1 / orderedKeys.length;
  const isExplicit = (k) => {
    const v = Number(q[k]);
    return Object.prototype.hasOwnProperty.call(q, k) && Number.isFinite(v) && v >= 0;
  };
  const explicitKeys = orderedKeys.filter(isExplicit);
  const fallback = explicitKeys.length > 0
    ? explicitKeys.reduce((a, k) => a + Number(q[k]), 0) / explicitKeys.length
    : equalShare;

  const raw = {};
  orderedKeys.forEach((k) => { raw[k] = isExplicit(k) ? Number(q[k]) : fallback; });

  const total = orderedKeys.reduce((a, k) => a + raw[k], 0);
  const weights = {};
  orderedKeys.forEach((k) => { weights[k] = total > 0 ? raw[k] / total : equalShare; });
  return { orderedKeys, weights };
};

/**
 * Compute a Star Score (0–100) for a single tutor given pre-aggregated stats and a formula.
 *
 * stats shape:
 * {
 *   dimensions: { [dimensionKey]: 0–100 }  // whatever dimensions the org's rubric defines;
 *                                          // omit a key (or pass null) when it has no rating
 *   qaCount: number  // how many QA rows backed the dimensions
 *   volumeShare: number  // 0–1, this tutor's hours / max hours across the org
 *   issueRate: number  // openIssues / sessions, clamped 0–1 by caller or here
 *   onTimePct: number  // 0–100
 * }
 */
export const computeStarScore = (stats, rawFormula) => {
  const formula = normalizeFormula(rawFormula);
  const { weights, quality_dimensions } = formula;

  const topSum = sumValues(weights);
  const topNorm = topSum > 0
    ? Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, v / topSum]))
    : { quality: 0, volume: 0, issue_free: 0, punctuality: 0 };

  const s = stats || {};
  const d = s.dimensions || {};
  const ratedKeys = Object.keys(d).filter((k) => isRatedValue(d[k]));
  const { orderedKeys, weights: dimNorm } = resolveDimensionWeights(quality_dimensions, ratedKeys);

  const qualityScore = s.qaCount > 0 && orderedKeys.length > 0
    ? orderedKeys.reduce((acc, k) => acc + (Number(d[k]) || 0) * (dimNorm[k] || 0), 0) / 100
    : 0;

  const volumeScore = Math.max(0, Math.min(1, Number(s.volumeShare) || 0));
  const issueScore = Math.max(0, 1 - Math.min(Number(s.issueRate) || 0, 1));
  const punctualityScore = Math.max(0, Math.min(1, (Number(s.onTimePct) || 0) / 100));

  const composite =
    qualityScore * (topNorm.quality || 0) +
    volumeScore * (topNorm.volume || 0) +
    issueScore * (topNorm.issue_free || 0) +
    punctualityScore * (topNorm.punctuality || 0);

  return Math.round(composite * 100);
};

/**
 * Human-readable summary of a formula.
 *
 * `dimensions` is optional and backwards compatible: pass the org's rubric
 * ([{ key, label }] from resolveRubricDimensions) to describe the sub-weights
 * that actually apply to that rubric — including custom keys, and using the
 * org's own labels. Omit it and the output is the historical one: every key in
 * the formula, in formula order, with humanised labels.
 */
export const formulaPreview = (rawFormula, dimensions) => {
  const f = normalizeFormula(rawFormula);
  const pct = (v) => `${Math.round(v * 100)}%`;
  const w = f.weights;
  const q = f.quality_dimensions;

  const rubric = Array.isArray(dimensions) && dimensions.length > 0
    ? dimensions.filter(d => d && typeof d.key === 'string' && d.key)
    : null;

  let qBits;
  if (rubric) {
    const { orderedKeys, weights: dimNorm } = resolveDimensionWeights(q, rubric.map(d => d.key));
    const labelFor = (key) => {
      const hit = rubric.find(d => d.key === key);
      return (hit && typeof hit.label === 'string' && hit.label.trim()) || humaniseKey(key);
    };
    qBits = orderedKeys.map(k => `${labelFor(k)} ${pct(dimNorm[k] || 0)}`).join(' + ');
  } else {
    qBits = Object.keys(q).map(k => `${humaniseKey(k)} ${pct(Number(q[k]) || 0)}`).join(' + ');
  }
  return `Star Score = Quality(${pct(w.quality)}: ${qBits}) + Volume(${pct(w.volume)}) + Issue-free(${pct(w.issue_free)}) + Punctuality(${pct(w.punctuality)})`;
};
